/**
 * f11.6: the banner's client-side filter -- one code chip, or "only lines with
 * warnings". Applied AFTER the toolbar filters (f11.4's client included), so
 * the two compose; the totals and stat cards stay unit totals. Held in state,
 * not in the query data, so it survives every refetch.
 */

import { useCallback, useState } from 'react';

import type { ForecastLine, ValidationWarningCode } from '@/api/client';

export interface WarningFilter {
  code: ValidationWarningCode | null;
  onlyWarnings: boolean;
}

export const NO_WARNING_FILTER: WarningFilter = { code: null, onlyWarnings: false };

export function applyWarningFilter(lines: readonly ForecastLine[], filter: WarningFilter): ForecastLine[] {
  if (filter.code) {
    const code = filter.code;
    return lines.filter((line) => (line.validation_warnings ?? []).some((w) => w.code === code));
  }
  if (filter.onlyWarnings) return lines.filter((line) => (line.validation_warnings ?? []).length > 0);
  return [...lines];
}

export function useWarningFilter() {
  const [filter, setFilter] = useState<WarningFilter>(NO_WARNING_FILTER);
  const toggleCode = useCallback(
    (code: ValidationWarningCode) => setFilter((current) => ({ ...current, code: current.code === code ? null : code })),
    [],
  );
  const setOnlyWarnings = useCallback(
    (onlyWarnings: boolean) => setFilter((current) => ({ ...current, onlyWarnings })),
    [],
  );
  const reset = useCallback(() => setFilter(NO_WARNING_FILTER), []);
  return { filter, toggleCode, setOnlyWarnings, reset };
}
